import { useEffect, useState } from "react"
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from "@mui/material"
import { useParams } from "react-router"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Bound } from "lib/@components/binding/Bound"
import { generate } from "library/guid/index.mjs"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { scenario } from "routes/scenarios/controller/scenarios"
import { scenarioRuleSet } from "routes/scenarios/controller/scenario-rule-set"
import { updateScenarioRuleSet } from "routes/scenarios/controller/update-scenario-rule-set"
import { regimeConditionData } from "routes/rule-sets/controller/regime-condition-data"
import { assetRegisterConditionData } from "routes/rule-sets/controller/asset-register-condition-data"
import { RuleSetNameSection } from "../rule-sets/rule-set-name-section"
import { DefaultActionSelector } from "../rule-sets/default-action-selector"
import { RuleListEditor } from "../rule-sets/rule-list-editor"
import { ErrorAlert } from "../rule-sets/error-alert"
import { WarningAlert } from "../rule-sets/warning-alert"
import { DataContextSection } from "../rule-sets/data-context-section"
import { validateRuleSet } from "../rule-sets/validate-rule-set"
import { SCENARIO_CONDITION_FIELDS } from "./scenario-condition-fields"

export function ScenarioRuleSetEditorView() {
    const { id } = useParams()
    const scenarioData = scenario.useResultsOnce({ id }, withInitialValue(IS_LOADING))
    const ruleSetData = scenarioRuleSet.useResults({ scenarioId: id }, withInitialValue(IS_LOADING))

    const [name, setName] = useState("")
    const [description, setDescription] = useState("")
    const [defaultAction, setDefaultAction] = useState("include")
    const [rules, setRules] = useState([])
    const [saveError, setSaveError] = useState(null)
    const [saving, setSaving] = useState(false)
    const [initialized, setInitialized] = useState(false)
    const [selectedRegime, setSelectedRegime] = useState(null)
    const [selectedRegister, setSelectedRegister] = useState(null)

    const regimeData = regimeConditionData.useResults(selectedRegime ? { regimeId: selectedRegime.id } : null)
    const registerData = assetRegisterConditionData.useResults(
        selectedRegister ? { registerId: selectedRegister.id } : null
    )

    const mergedRegimeData = mergeRegimeAndRegisterData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(initializeFromRuleSet, [ruleSetData])

    const validation = validateRuleSet({ name, description, defaultAction, rules })

    const isLoading = scenarioData === IS_LOADING || ruleSetData === IS_LOADING

    if (isLoading) return <LoadingScreen description="Loading scenario rules..." />

    if (!scenarioData) {
        return (
            <PageBreadcrumbContainer title="Scenario Not Found" parentCrumbs={buildParentCrumbs()} sx={{ height: 1 }}>
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                        This scenario could not be found.
                    </Typography>
                </Stack>
            </PageBreadcrumbContainer>
        )
    }

    if (!ruleSetData) {
        return (
            <PageBreadcrumbContainer
                title="Scenario Rules Not Found"
                parentCrumbs={buildParentCrumbs()}
                sx={{ height: 1 }}
            >
                <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, p: 3 }}>
                    <Typography variant="h6" color="text.secondary">
                        No scenario rule set found. Add one from the scenario view.
                    </Typography>
                </Stack>
            </PageBreadcrumbContainer>
        )
    }

    return (
        <PageBreadcrumbContainer title="Scenario Rules" parentCrumbs={buildParentCrumbs()} sx={{ height: 1 }}>
            <Bound conditionFields={SCENARIO_CONDITION_FIELDS} regimeData={mergedRegimeData}>
                <Stack data-cy="scenario-rule-set-editor" spacing={3} sx={{ px: 3, pt: 2, pb: 4, flex: 1 }}>
                    <RuleSetNameSection
                        name={name}
                        description={description}
                        onNameChange={setName}
                        onDescriptionChange={setDescription}
                    />
                    <Divider />
                    <DataContextSection
                        selectedRegime={selectedRegime}
                        onRegimeChange={setSelectedRegime}
                        selectedRegister={selectedRegister}
                        onRegisterChange={setSelectedRegister}
                    />
                    <Divider />
                    <DefaultActionSelector value={defaultAction} onChange={setDefaultAction} />
                    <If value={validation.errors.length > 0}>
                        <Repeat list={validation.errors} item={<ErrorAlert />} />
                    </If>
                    <If value={validation.warnings.length > 0}>
                        <Repeat list={validation.warnings} item={<WarningAlert />} />
                    </If>
                    <RuleListEditor
                        rules={rules}
                        onAdd={handleAddRule}
                        onUpdate={handleUpdateRule}
                        onRemove={handleRemoveRule}
                        onReorder={handleReorder}
                        helperText="Rules are evaluated in order. The first matching rule determines the action for each task. Scenario context fields (facility, location, asset type, classification, trade) are available."
                    />
                    <Divider />
                    <If value={saveError}>
                        <Alert severity="error">{saveError}</Alert>
                    </If>
                    <Box sx={{ display: "flex", gap: 2 }}>
                        <Button
                            data-cy="scenario-rules-save"
                            variant="contained"
                            startIcon={
                                <If value={saving}>
                                    <CircularProgress size={18} color="inherit" />
                                </If>
                            }
                            onClick={handleSave}
                            disabled={validation.errors.length > 0 || saving}
                        >
                            Save Scenario Rules
                        </Button>
                    </Box>
                </Stack>
            </Bound>
        </PageBreadcrumbContainer>
    )

    function buildParentCrumbs() {
        return [
            { label: "Optimiser", to: "/app/optimiser" },
            { label: scenarioData?.name || "Scenario", to: `/app/optimiser/scenarios/${id}` },
        ]
    }

    function mergeRegimeAndRegisterData() {
        if (!regimeData && !registerData) return null
        const base = regimeData || {
            facilities: [],
            towns: [],
            postcodes: [],
            assetTypes: [],
            regimeFields: [],
            assetRegisterFields: [],
        }
        if (!registerData?.assetRegisterFields?.length) return base
        return {
            ...base,
            assetRegisterFields: [...(base.assetRegisterFields || []), ...registerData.assetRegisterFields],
        }
    }

    function initializeFromRuleSet() {
        if (ruleSetData === IS_LOADING || !ruleSetData || initialized) return
        setName(ruleSetData.name)
        setDescription(ruleSetData.description || "")
        setDefaultAction(ruleSetData.defaultAction || "include")
        setRules(toEditableRules(ruleSetData.rules))
        setInitialized(true)
    }

    function toEditableRules(serverRules) {
        return (serverRules || []).map((r) => ({
            ...r,
            conditions: (r.conditions || []).map((c) => ({ _id: generate(), ...c })),
            action: r.action ? { ...r.action } : { type: "include" },
        }))
    }

    function handleReorder(reordered) {
        setRules(reordered)
    }

    function handleAddRule(newRule) {
        setRules((prev) => [...prev, newRule])
    }

    function handleUpdateRule(ruleId, updatedRule) {
        setRules((prev) => prev.map((r) => (r.id === ruleId ? { ...r, ...updatedRule } : r)))
    }

    function handleRemoveRule(ruleId) {
        setRules((prev) => prev.filter((r) => r.id !== ruleId))
    }

    async function handleSave() {
        setSaveError(null)
        setSaving(true)
        try {
            await updateScenarioRuleSet({
                scenarioId: id,
                data: { name, description, defaultAction, rules: stripTypenames(rules) },
            })
            successSnackbar("Scenario rules saved")
        } catch (e) {
            setSaveError(e.message || "Failed to save scenario rules")
        } finally {
            setSaving(false)
        }
    }

    function stripTypenames(rulesToStrip) {
        return rulesToStrip.map(({ __typename, conditions, action, ...rest }) => ({
            ...rest,
            conditions: (conditions || []).map(({ __typename: __tn, _id: __cid, ...c }) => c),
            action: action ? (({ __typename: __tn2, ...a }) => a)(action) : action,
        }))
    }
}
